import type { ReviewQueueItem } from '@eriko/shared-types';
import { formatCurrency } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { useReconciliationStore } from '../../stores/reconciliation';

interface Props {
  item: ReviewQueueItem;
}

function confidenceVariant(
  conf: number | undefined,
): 'success' | 'info' | 'warning' | 'secondary' {
  if (conf == null) return 'secondary';
  if (conf >= 0.92) return 'success';
  if (conf >= 0.75) return 'info';
  if (conf >= 0.6) return 'warning';
  return 'secondary';
}

export function QueueItemRow({ item }: Props) {
  const toggleSelected = useReconciliationStore((s) => s.toggleSelected);
  const openDrawer = useReconciliationStore((s) => s.openDrawer);
  const isSelected = useReconciliationStore((s) =>
    item.matchId ? s.selectedMatchIds.has(item.matchId) : false,
  );

  const tx = item.bankTransaction;
  const isCredit = tx.type === 'CREDIT';
  const amountStr = `${isCredit ? '+' : '-'}${formatCurrency(
    Math.abs(parseFloat(tx.amount)),
    2,
    tx.currency ?? undefined,
  )}`;
  // Already-CONFIRMED matches can't be bulk-confirmed (it would clobber
  // their original audit trail). Hide the checkbox to disable selection;
  // the row still shows + can be opened in the drawer for inspection.
  const isAlreadyConfirmed = item.matchStatus === 'CONFIRMED';

  return (
    <li className="flex items-center gap-3 px-4 py-3 hover:bg-surface-50">
      {item.matchId && !isAlreadyConfirmed ? (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelected(item.matchId!)}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-surface-300 text-surface-900 focus:ring-surface-500"
        />
      ) : (
        <div className="w-4" />
      )}

      <button
        type="button"
        onClick={() => item.matchId && openDrawer(item.matchId)}
        className="flex-1 flex items-center gap-4 text-left min-w-0"
      >
        <div className="w-20 shrink-0 text-xs text-surface-500">
          {new Date(tx.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-surface-900 truncate">
            {tx.description || tx.rawDescription || '(no description)'}
          </div>
          {tx.merchantName && (
            <div className="text-xs text-surface-500 truncate">{tx.merchantName}</div>
          )}
          {item.matchedObjectSummary && (
            <div className="text-xs text-surface-400 truncate">{item.matchedObjectSummary}</div>
          )}
        </div>
        <div
          className={`w-24 text-right text-sm font-mono ${
            isCredit ? 'text-emerald-700' : 'text-surface-900'
          }`}
        >
          {amountStr}
        </div>
        {item.topConfidence != null && (
          <Badge variant={confidenceVariant(item.topConfidence)}>
            {(item.topConfidence * 100).toFixed(0)}%
          </Badge>
        )}
        {item.matchedObjectSummary && item.variance === 0 && (
          <Badge variant="success">Matches bank line</Badge>
        )}
        {item.variance !== undefined && item.variance !== 0 && (
          <Badge variant="warning">
            Variance: {item.variance > 0 ? '+' : ''}
            {formatCurrency(Math.abs(item.variance), 2, tx.currency ?? undefined)}
          </Badge>
        )}
        {isAlreadyConfirmed && <Badge variant="success">Confirmed</Badge>}
        {item.flag && (
          <Badge variant="warning">
            {item.flag.type.replace(/_/g, ' ').toLowerCase()}
          </Badge>
        )}
      </button>
    </li>
  );
}
